import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { exportAchatEvolutionExcel } from "./stats-achat-evolution-excel.utils";
import { NcAsideBlockComponent } from "../../../non-conformite/components/nc-aside-block/nc-aside-block.component";
import { NonConformiteFacadeService } from "../../../non-conformite/services/non-conformite/non-conformite-facade.service";

export interface AchatEvolutionLot {
  lotId: string | null;
  lotNumero: string;
  qtyUG: number;
  uniteGestion: string;
  puAchat: number;
  coutTotal: number;
}

export interface AchatEvolutionCommandeRow {
  commandeId: string;
  numero: string;
  dateKey: string;
  qtyUG: number;
  puAchatMoyen: number;
  coutTotal: number;
  lots: AchatEvolutionLot[];
}

export interface AchatEvolutionDialogData {
  articleDenomination: string;
  articleUniteGestion: string;
  tiersDenomination: string;
  tiersId?: string;
  articleId?: string;
  commandes: AchatEvolutionCommandeRow[];
}

type ViewMode = "byCommande" | "byMonth";

interface Stats {
  puMoyen: number;
  puMin: number;
  puMax: number;
  variation: number;
  variationFormatted: string;
  nbCommandes: number;
  qtyTotale: number;
  coutTotal: number;
  coutTotalFormatted: string;
  puMoyenFormatted: string;
  minMaxFormatted: string;
}

interface MonthRow {
  monthKey: string;
  monthLabel: string;
  nbCommandes: number;
  qtyUG: number;
  coutTotal: number;
  puAchatMoyen: number;
}

@Component({
  selector: "vl-stats-achat-evolution-dialog",
  standalone: true,
  imports: [
    CommonModule, SharedModule, DialogHeaderExportComponent,
    DecimalPipe, MatIcon, MatTooltip,
    RadioCardComponent, LotCardComponent, ChipsToggleComponent,
    NcAsideBlockComponent,
  ],
  templateUrl: "./stats-achat-evolution-dialog.component.html",
  styleUrl: "./stats-achat-evolution-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsAchatEvolutionDialogComponent {
  data = inject<AchatEvolutionDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<StatsAchatEvolutionDialogComponent>);

  expanded = signal<Set<string>>(new Set());

  viewMode = signal<ViewMode>("byCommande");
  viewModeElements = [
    { name: "Par commande", value: "byCommande" as ViewMode },
    { name: "Par mois", value: "byMonth" as ViewMode },
  ];

  toggle(key: string) {
    const current = new Set(this.expanded());
    current.has(key) ? current.delete(key) : current.add(key);
    this.expanded.set(current);
  }

  isOpen(key: string): boolean { return this.expanded().has(key); }

  sortedCommandes = computed<AchatEvolutionCommandeRow[]>(() =>
    [...this.data.commandes].sort((a, b) =>
      a.dateKey === b.dateKey
        ? a.numero.localeCompare(b.numero)
        : b.dateKey.localeCompare(a.dateKey),
    ),
  );

  monthRows = computed<MonthRow[]>(() => {
    const map = new Map<string, MonthRow>();
    const fmtFR = new Intl.DateTimeFormat("fr-FR", { year: "numeric", month: "long" });
    for (const c of this.data.commandes) {
      const d = new Date(c.dateKey);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(monthKey)) {
        let label = monthKey;
        try { label = fmtFR.format(d); } catch { /* ignore */ }
        map.set(monthKey, {
          monthKey, monthLabel: label,
          nbCommandes: 0, qtyUG: 0, coutTotal: 0, puAchatMoyen: 0,
        });
      }
      const m = map.get(monthKey)!;
      m.nbCommandes++;
      m.qtyUG += c.qtyUG;
      m.coutTotal += c.coutTotal;
    }
    const rows = [...map.values()];
    for (const m of rows) {
      m.puAchatMoyen = m.qtyUG !== 0 ? m.coutTotal / m.qtyUG : 0;
    }
    return rows.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  });

  stats = computed<Stats>(() => {
    const cs = this.data.commandes;
    if (!cs.length) {
      return {
        puMoyen: 0, puMin: 0, puMax: 0, variation: 0, variationFormatted: "—",
        nbCommandes: 0, qtyTotale: 0, coutTotal: 0,
        coutTotalFormatted: "—", puMoyenFormatted: "—", minMaxFormatted: "—",
      };
    }
    let qtyTotale = 0, coutTotal = 0;
    let puMin = Infinity, puMax = -Infinity;
    for (const c of cs) {
      qtyTotale += c.qtyUG;
      coutTotal += c.coutTotal;
      if (c.qtyUG > 0) {
        if (c.puAchatMoyen < puMin) puMin = c.puAchatMoyen;
        if (c.puAchatMoyen > puMax) puMax = c.puAchatMoyen;
      }
    }
    const puMoyen = qtyTotale !== 0 ? coutTotal / qtyTotale : 0;
    const chrono = [...cs].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    const first = chrono[0]?.puAchatMoyen ?? 0;
    const last = chrono.at(-1)?.puAchatMoyen ?? 0;
    const variation = first > 0 ? ((last - first) / first) * 100 : 0;
    const sign = variation > 0 ? "+" : "";
    return {
      puMoyen,
      puMin: Number.isFinite(puMin) ? puMin : 0,
      puMax: Number.isFinite(puMax) ? puMax : 0,
      variation,
      variationFormatted: `${sign}${variation.toFixed(1)}%`,
      nbCommandes: cs.length,
      qtyTotale,
      coutTotal,
      coutTotalFormatted: formatEuroShort(coutTotal),
      puMoyenFormatted: `${puMoyen.toFixed(2)} €`,
      minMaxFormatted: Number.isFinite(puMin) && Number.isFinite(puMax)
        ? `${puMin.toFixed(2)} € → ${puMax.toFixed(2)} €`
        : "—",
    };
  });

  colorForPU(pu: number): string {
    const mean = this.stats().puMoyen;
    if (!mean) return "#9db8c3";
    const ratio = pu / mean;
    if (ratio < 0.85) return "#10b981";
    if (ratio < 1.0) return "#facc15";
    if (ratio < 1.15) return "#f97316";
    return "#dc2626";
  }

  variationColor = computed(() => {
    const v = this.stats().variation;
    if (v > 0.5) return "error";
    if (v < -0.5) return "valid";
    return "secondary";
  });

  formatDate(dateKey: string): string {
    try {
      return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        .format(new Date(dateKey));
    } catch { return dateKey; }
  }

  formatEuro(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  exporting = signal(false);
  private ncFacade = inject(NonConformiteFacadeService);

  async exportExcel() {
    this.exporting.set(true);
    try {
      const ncs = await this.loadNcs();
      await exportAchatEvolutionExcel(this.data, ncs);
    } finally {
      this.exporting.set(false);
    }
  }

  /**
   * Charge les NC pour l'onglet Excel — intersection tiers ∩ article si
   * les deux ids sont fournis (cas dialog évolution achat d'un article
   * pour un fournisseur). Sinon par tiers ou par article seul.
   */
  private async loadNcs() {
    if (this.data.tiersId && this.data.articleId) {
      const all = await this.ncFacade.loadByTiers(this.data.tiersId);
      return (all ?? []).filter((nc) => nc.articleId === this.data.articleId);
    }
    if (this.data.tiersId) return this.ncFacade.loadByTiers(this.data.tiersId);
    if (this.data.articleId) return this.ncFacade.loadByArticle(this.data.articleId);
    return undefined;
  }
}

function formatEuroShort(v: number): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M€`;
  if (abs >= 10_000)
    return `${(n / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k€`;
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}
